import { useEffect, useRef, useState, type ChangeEvent, type FocusEvent, type InputHTMLAttributes, type TextareaHTMLAttributes } from 'react';

type Autres = Omit<InputHTMLAttributes<HTMLInputElement> & TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'defaultValue' | 'onChange' | 'onBlur'>;

interface Props extends Autres {
  valeur: string;
  /** Appelée quand la frappe se pose (250 ms sans touche) ou à la sortie du champ : la page ne se redessine pas à chaque lettre. */
  onValider: (texte: string) => void;
  /** Une zone de texte (sinon un champ d'une ligne). */
  multiligne?: boolean;
  /** À la sortie du champ, avec le texte tel qu'il est. */
  onSortie?: (texte: string) => void;
}

/**
 * Champ de texte qui ne prévient la page qu'une fois la frappe posée. Toute la page « Nouvelle demande » se redessine à chaque changement d'état : une lettre
 * à la fois, cela se sent sur un poste ancien (une centaine de millisecondes par lettre mesurées avec un processeur 4 fois plus lent). La valeur est toujours
 * posée à la sortie du champ, donc avant tout clic sur « Envoyer ».
 */
export function TexteDiffere({ valeur, onValider, multiligne, onSortie, ...rest }: Props) {
  const [local, setLocal] = useState(valeur);
  const frappe = useRef(false);   // des lettres ont été tapées et pas encore posées : ne pas écraser le champ
  const minuteur = useRef<number | undefined>(undefined);
  useEffect(() => { if (!frappe.current) setLocal(valeur); }, [valeur]);
  useEffect(() => () => window.clearTimeout(minuteur.current), []);

  const commun = {
    ...rest,
    value: local,
    onChange: (e: ChangeEvent<HTMLInputElement & HTMLTextAreaElement>) => {
      const texte = e.target.value;
      frappe.current = true;
      setLocal(texte);
      window.clearTimeout(minuteur.current);
      minuteur.current = window.setTimeout(() => { frappe.current = false; onValider(texte); }, 250);
    },
    onBlur: (e: FocusEvent<HTMLInputElement & HTMLTextAreaElement>) => {
      const texte = e.target.value;
      window.clearTimeout(minuteur.current);
      frappe.current = false;
      if (texte !== valeur) onValider(texte);
      onSortie?.(texte);
    },
  };
  return multiligne ? <textarea {...commun} /> : <input {...commun} />;
}
